import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { AnalysisVersionResponse } from '@api-response-types/analysis-version.response';
import { CreateAnalysisConfigurationDto } from '@api-types/dtos/create-analysis-configuration.dto';
import { AnalysisConfigurationResponse } from '@api-types/responses/analysis-configuration.response';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { AnalysisNavigationService } from 'demand-forecasting/analysis/services/analysis-navigation.service';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import { selectAnalysisVersionIdByQueryParam } from 'demand-forecasting/store/router-state/router.selectors';
import { finalize, map, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AnalysisMutationsService {
  private readonly http = inject(HttpClient);
  private readonly analysisStateService = inject(AnalysisStateService);
  private readonly store = inject(Store);
  private readonly analysisNavigationService = inject(
    AnalysisNavigationService,
  );

  private readonly analysisVersionId = this.store.selectSignal(
    selectAnalysisVersionIdByQueryParam,
  );

  // loading states
  createAnalysisConfigurationLoading = signal(false);
  createAnalysisVersionLoading = signal(false);

  public createAnalysisConfiguration(
    configurationId: string,
    dto: CreateAnalysisConfigurationDto,
  ) {
    return this.http
      .post<AnalysisConfigurationResponse>(
        `${environment.api.url}/analysis/analysis-configurations/configurations/${configurationId}`,
        dto,
      )
      .pipe(
        tap(() => this.createAnalysisConfigurationLoading.set(true)),
        map((res) => {
          const oldAnalysisConfigurationsState =
            this.analysisStateService.configurations.value();
          if (oldAnalysisConfigurationsState) {
            this.analysisStateService.configurations.set([
              res,
              ...oldAnalysisConfigurationsState,
            ]);
          } else {
            this.analysisStateService.configurations.reload();
          }

          this.analysisNavigationService.setQueryParams({
            analysisConfigurationId: res.id,
            versionId: res.latestAnalysisVersion?.id ?? null,
            hierarchy: null,
            filter: null,
          });
        }),
        finalize(() => this.createAnalysisConfigurationLoading.set(false)),
      );
  }

  public createAnalysisVersion(analysisConfigurationId: string) {
    return this.http
      .post<AnalysisVersionResponse>(
        `${environment.api.url}/analysis/analysis-configurations/${analysisConfigurationId}/versions`,
        {},
      )
      .pipe(
        tap(() => this.createAnalysisVersionLoading.set(true)),
        map((res) => {
          const oldAnalysisVersionsState =
            this.analysisStateService.versions.value();
          if (!oldAnalysisVersionsState) {
            this.analysisStateService.versions.reload();
            return;
          }
          // add the new configuration to the state
          const updatedAnalysisVersionsState = [
            res,
            ...oldAnalysisVersionsState,
          ];
          this.analysisStateService.versions.set(updatedAnalysisVersionsState);

          // keep configurations in sync so latestAnalysisVersion is up to date
          this.analysisStateService.configurations.reload();

          // invalidate related queries
          this.analysisStateService.aggregatedVersionGroup.reload();
          this.analysisStateService.tableVersionGroups.reload();

          return res;
        }),
        finalize(() => this.createAnalysisVersionLoading.set(false)),
      );
  }

  public deleteAnalysisVersion(versionId: string) {
    // find next version to select before deleting
    const currentVersions = this.analysisStateService.versions.value() ?? [];
    const remainingVersion = currentVersions.find((v) => v.id !== versionId);
    const nextVersion = remainingVersion ?? null;

    return this.http
      .delete<HttpStatusCode>(
        `${environment.api.url}/analysis/analysis-versions/${versionId}`,
      )
      .pipe(
        map(() => {
          this.analysisStateService.versions.reload();

          // keep configurations in sync so latestAnalysisVersion is up to date
          this.analysisStateService.configurations.reload();

          // if deleted version was selected, navigate to next available version
          if (this.analysisVersionId() === versionId) {
            this.analysisNavigationService.setQueryParams({
              filter: null,
              hierarchy: null,
              versionId: nextVersion?.id ?? null,
            });
          }
        }),
      );
  }

  public deleteAnalysisConfiguration(analysisConfigurationId: string) {
    return this.http
      .delete<HttpStatusCode>(
        `${environment.api.url}/analysis/analysis-configurations/${analysisConfigurationId}`,
      )
      .pipe(
        map(() => {
          this.analysisNavigationService.setQueryParams({
            filter: null,
            hierarchy: null,
            versionId: null,
            analysisConfigurationId: null,
          });
          this.analysisStateService.configurations.reload();
          this.analysisStateService.versions.reload();
        }),
      );
  }
}
